<template>
  <div class="flex flex-col gap-4 p-4 bg-amber-50/70 rounded-xl border border-amber-200/50">
    <div class="flex items-start gap-3">
      <div class="text-2xl">🔑</div>
      <div class="flex-1">
        <h4 class="text-sm font-semibold text-vault-text-primary mb-1">
          {{ $t('mnemonicReveal.title') }}
        </h4>
        <p class="text-xs text-vault-text-secondary mb-3 leading-relaxed">
          {{ contextMessage }}
        </p>

        <div
          v-if="isRevealed"
          class="relative p-3 bg-white rounded-lg border border-amber-200 mb-3"
        >
          <p class="font-mono text-sm text-vault-text-primary leading-relaxed break-all select-all">
            {{ mnemonic }}
          </p>
          <button
            type="button"
            class="absolute top-2 right-2 p-1.5 rounded-md hover:bg-amber-50 transition-colors"
            @click="handleCopy"
            :title="copyTooltip"
          >
            <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-vault-success-500">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>
        </div>

        <div class="flex gap-2">
          <button
            type="button"
            class="btn-ghost text-xs px-3 py-2"
            @click="toggleReveal"
          >
            {{ isRevealed ? $t('mnemonicReveal.hide') : $t('mnemonicReveal.reveal') }}
          </button>
          <button
            v-if="isRevealed"
            type="button"
            class="btn-ghost text-xs px-3 py-2"
            @click="handleCopy"
          >
            {{ copied ? $t('mnemonicReveal.copied') : $t('mnemonicReveal.copy') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = withDefaults(defineProps<{
  mnemonic: string;
  context?: 'active' | 'spent';
}>(), {
  context: 'active'
});

const emit = defineEmits<{
  copy: [];
}>();

const isRevealed = ref(false);
const copied = ref(false);
let copyTimeout: ReturnType<typeof setTimeout> | null = null;

const contextMessage = computed(() => {
  if (props.context === 'spent') {
    return $t('mnemonicReveal.spentMessage');
  }
  return $t('mnemonicReveal.activeMessage');
});

const copyTooltip = computed(() => {
  return copied.value ? $t('mnemonicReveal.copied') : $t('mnemonicReveal.copy');
});

function toggleReveal() {
  isRevealed.value = !isRevealed.value;
}

async function handleCopy() {
  try {
    await navigator.clipboard.writeText(props.mnemonic);
    copied.value = true;
    emit('copy');

    if (copyTimeout) {
      clearTimeout(copyTimeout);
    }

    copyTimeout = setTimeout(() => {
      copied.value = false;
      copyTimeout = null;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy mnemonic:', err);
  }
}
</script>

<style scoped>
/* All styles handled by Tailwind */
</style>
